import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
  FormControl,
  TextField,
  Button,
  Link,
  FormLabel,
  Grid2
} from '@mui/material';
import CommonWrapper from "Components/Common/CommonWrapper";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

// Helper component for form fields to reduce repetition
const FormField = ({ label, fullWidth = false }) => (
  <Grid item xs={12} md={fullWidth ? 12 : 6}>
    <Stack spacing={0.5}>
      <FormLabel label> {/* The `label` prop activates custom styles from your theme.js */}
        {label}
        <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
      </FormLabel>
      <TextField
        fullWidth
        placeholder="Enter Your Email" // Replicating the placeholder from the image
      />
    </Stack>
  </Grid>
);

// Helper for styled list items inside the info box
const InfoListItem = ({ children, color = 'secondary.light' }) => (
  <Box component="li" sx={{ color, '&::marker': { color } }}>
    <Typography variant="body1" component="span" color="inherit">
      {children}
    </Typography>
  </Box>
);

const PersonalDetails = () => {
    const dispatch = useDispatch();
    const { Translation, Common } = useSelector((state) => ({
        Common: state.Common.common || {},
        Translation: state.Translation.translation?.PersonalDetails || {},
    }));

    const Schema = useMemo(() => {
        return yup.object({
          AccountHolderName: yup.string().required(Translation?.AccountHolderNameRequired || "Account holder name is required field."),
          PermanentAddress: yup.string().required(Translation?.PermanantAddressRequired || "Permanant address is required field."),
          AccountNumber: yup.string().required(Translation?.AccountNumberRequired || "Account number is required field."),
          IFSCCode: yup.string().required(Translation?.IFSCCodeRequired || "IFSC code is required field."),
          PanNumber: yup.string().required(Translation?.PanNumberRequired || "PAN number is required field.")
        });
    }, [Translation]) 
    
    const {
        dirty,
        values,
        setValues,
        handleBlur,
        handleChange,
        handleSubmit,
        errors,
        resetForm,
        touched,
        submitCount,
        setFieldTouched,
        setFieldValue,
      } = useFormik({
        initialValues: {},
        enableReinitialize: true,
        onSubmit: (data) => {
          console.log("data", data);
          const personalObj = {
            FullName: data.FullName,
            Address: data.Address,
            AccountNumber: data.AccountNumber,
            IFSCCode: data.IFSCCode,
            PanNumber: data.PanNumber,
          }
        //   dispatch(updateProfile(personalObj));
        },
        validationSchema: Schema,
    });

  return (
    <CommonWrapper width="lg">
        <Box>
            <Stack spacing={3} maxWidth="lg" mx="auto">
                
                <Typography variant="h6" fontWeight="bold">
                    Payment Details and Document Upload
                </Typography>

                {/* Information Box */}
                <Paper
                elevation={0}
                sx={{
                    p: 3,
                    bgcolor: 'yellow.main',
                    borderRadius: 2,
                    '& ol': { pl: '20px', my: 1.5, display: 'flex', flexDirection: 'column', gap: 1 },
                }}
                >
                <Stack spacing={2}>
                    <Box>
                    <Typography variant="body1" color="secondary.light" fontWeight="500">
                        Why do we need your account and personal details:
                    </Typography>
                    <Box component="ol">
                        <InfoListItem>
                        We want these details to reward you if your referral code is ever applied by an incoming learner looking to join Scaler Academy. To know more, please visit <Link href="#" underline="none">Link</Link>
                        </InfoListItem>
                        <InfoListItem>
                        During the course of the program, you might also win exciting goodies and prizes. You will receive those rewards at the bank account / address you enter below
                        </InfoListItem>
                    </Box>
                    </Box>

                    <Box>
                    <Typography variant="body1" color="secondary.light" fontWeight="500">
                        Why do we need your ID proof, employment proof (either employer badge, offer letter) and resume (don't worry if its not completely up to date, you will refine this via Mentor sessions in the program!):
                    </Typography>
                    <Box component="ol">
                        <InfoListItem>
                        Verify your identity (we take regular snapshots of the test-taker during our Coding Challenge through the course of the program)
                        </InfoListItem>
                        <InfoListItem>
                        Understand your current work experience so we can find the right job opportunities for you
                        </InfoListItem>
                    </Box>
                    </Box>

                    <Box>
                    <Typography variant="body1" color="secondary.light" fontWeight="bold">
                        Note:
                    </Typography>
                    <Box component="ol">
                        <InfoListItem color="error.main">
                        Make sure the bank account details and personal identification details are in your name (not your friends or relatives)
                        </InfoListItem>
                        <InfoListItem color="error.main">
                        Please do not change these details frequently
                        </InfoListItem>
                    </Box>
                    </Box>
                </Stack>
                </Paper>

                {/* Form Section */}
                <Box component="form" noValidate autoComplete="off">

                    <Grid2
                        container
                        spacing={2}
                    >
                        {/* <Grid2 size={6}> */}

                            <Grid2 size={6}>
                                <FormControl fullWidth>
                                    <FormLabel color="secondary" required label>
                                    {Translation?.AccountHolderName}
                                    </FormLabel>
                                    <TextField
                                    error={touched.AccountHolderName && errors.AccountHolderName}
                                    helperText={touched.AccountHolderName && errors.AccountHolderName && errors.AccountHolderName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="AccountHolderName"
                                    size="small"
                                    value={values.AccountHolderName}
                                    placeholder={Translation?.EnterYourAccountHolderName}
                                    />
                                </FormControl>
                            </Grid2>

                            <Grid2 size={6}>
                                <FormControl fullWidth>
                                    <FormLabel color="secondary" required label>
                                    {Translation?.PermanentAddress}
                                    </FormLabel>
                                    <TextField
                                    error={touched.PermanentAddress && errors.PermanentAddress}
                                    helperText={touched.PermanentAddress && errors.PermanentAddress && errors.PermanentAddress}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="PermanentAddress"
                                    size="small"
                                    value={values.PermanentAddress}
                                    placeholder={Translation?.EnterYourPermanentAddress}
                                    />
                                </FormControl>
                            </Grid2>


                            <Grid2 size={6}>
                                <FormControl fullWidth>
                                    <FormLabel color="secondary" required label>
                                    {Translation?.AccountNumber}
                                    </FormLabel>
                                    <TextField
                                    error={touched.AccountNumber && errors.AccountNumber}
                                    helperText={touched.AccountNumber && errors.AccountNumber && errors.AccountNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="AccountNumber"
                                    size="small"
                                    value={values.AccountNumber}
                                    placeholder={Translation?.EnterYourAccountNumber}
                                    />
                                </FormControl>
                            </Grid2>

                            <Grid2 size={6}>
                                <FormControl fullWidth>
                                    <FormLabel color="secondary" required label>
                                    {Translation?.IFSCCode}
                                    </FormLabel>
                                    <TextField
                                    error={touched.IFSCCode && errors.IFSCCode}
                                    helperText={touched.IFSCCode && errors.IFSCCode && errors.IFSCCode}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="IFSCCode"
                                    size="small"
                                    value={values.IFSCCode}
                                    placeholder={Translation?.EnterYourIFSCCode}
                                    />
                                </FormControl>
                            </Grid2>

                            <Grid2 size={6}>
                                <FormControl fullWidth>
                                    <FormLabel color="secondary" required label>
                                    {Translation?.PanNumber}
                                    </FormLabel>
                                    <TextField
                                    error={touched.PanNumber && errors.PanNumber}
                                    helperText={touched.PanNumber && errors.PanNumber && errors.PanNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="PanNumber"
                                    size="small"
                                    value={values.PanNumber}
                                    placeholder={Translation?.EnterYourPanNumber}
                                    />
                                </FormControl>
                            </Grid2>
                        {/* </Grid2> */}

                        {/* <Grid2 size={6}></Grid2> */}
                    </Grid2>


                    {/* <Grid container spacing={4}>
                        <FormField label="Account Holder Full Name" />
                        <FormField label="Permanent Address" />
                        <FormField label="Account Number" />
                        <FormField label="Ifsc Code" />
                        <FormField label="Pan Number" fullWidth />
                    </Grid> */}
                    <Button variant="contained" sx={{ mt: 4, px: 4 }}>
                        Submit
                    </Button>
                </Box>

            </Stack>
        </Box>
    </CommonWrapper>
  );
};

export default PersonalDetails;